<template>
  <section class="[ max-w-7xl m-auto min-h-full flex flex-col gap-6 ]">
    <div class="[ flex items-center justify-between ]">
      <h2 class="[ text-2xl font-semibold ]">Mockups</h2>
      <span
        class="[ text-xs font-medium px-2 py-1 rounded-full bg-amber-100 text-amber-700 ]"
      >
        Dev Only
      </span>
    </div>

    <p class="[ text-sm text-gray-dark ]">
      Prototypes and feature mockups. These use local mock data and are not
      connected to live APIs.
    </p>

    <div
      v-if="mockupPages.length === 0"
      class="[ flex flex-col items-center justify-center py-20 text-gray-dark ]"
    >
      <p class="[ text-lg ]">No mockups yet</p>
      <p class="[ text-sm mt-1 ]">
        Add pages to
        <code>src/pages/(app)/(logged-in)/mockups/</code>
      </p>
    </div>

    <div v-else class="[ grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ]">
      <RouterLink
        v-for="page in mockupPages"
        :key="page.path"
        :to="page.path"
        class="[ block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all ]"
      >
        <h3 class="[ text-base font-semibold ]">{{ page.title }}</h3>
        <p v-if="page.description" class="[ text-sm text-gray-dark mt-1 ]">
          {{ page.description }}
        </p>
        <span
          class="[ inline-block mt-3 text-xs font-medium px-2 py-0.5 rounded-full ]"
          :class="statusClass(page.status)"
        >
          {{ page.status }}
        </span>
      </RouterLink>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from "vue";

definePage({
  meta: {
    title: "Mockups",
  },
});

interface MockupEntry {
  path: string;
  title: string;
  description?: string;
  status: "draft" | "review" | "approved";
}

/**
 * Register mockup pages here.
 * Each entry links to a sibling .vue file in this directory.
 */
const mockupPages = ref<MockupEntry[]>([
  {
    path: "/mockups/scorecard-templates",
    title: "Scorecard Templates",
    description: "List view for managing scorecard templates with rules, versions, and status.",
    status: "draft",
  },
  {
    path: "/mockups/rules",
    title: "Rule Creation",
    description: "Author compliance rules with AI chat assistance and structured form side-by-side.",
    status: "draft",
  },
]);

function statusClass(status: MockupEntry["status"]) {
  const map: Record<string, string> = {
    draft: "bg-gray-100 text-gray-600",
    review: "bg-blue-100 text-blue-700",
    approved: "bg-green-100 text-green-700",
  };
  return map[status] ?? "";
}
</script>
